import React, {useEffect, useState} from 'react'
import {
  Avatar,
  Grid,
  IconButton,
  Paper
} from '@mui/material'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import PlayButton from '../components/PlayButton'
import {Card, Deck} from '../model/models'
import DeckApi from '../logic/DeckApi'
import {useNavigate, useParams} from 'react-router-dom'
import DeckLogic from '../logic/DeckLogic'
import EditIcon from '@mui/icons-material/Edit'
import {pink} from '@mui/material/colors'
import DeleteDialog from '../components/DeleteDialog'
import AddIcon from "@mui/icons-material/Add"
import ImageLogic from '../logic/ImageLogic'
import LoadingBackdrop from '../components/LoadingBackdrop'
import DeleteIcon from '@mui/icons-material/Delete'
import Button1 from '../components/Button1'
import ConsecutiveCorrect from '../components/ConsecutiveCorrect'
import {useSnackbar} from 'notistack'
import MarkdownDisplay from '../components/MarkdownDisplay'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import TagsDisplay from '../components/TagsDisplay'
import AnonAddDeckDialog from '../components/AnonAddDeckDialog'
import {useAuth} from '../components/auth/AuthProvider'

const api = new DeckApi()

export default function DeckPage({publicDeckView}: {publicDeckView: boolean}) {
  const [deck, setDeck] = useState<Deck | null>(null)
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const {id} = useParams()
  const navigate = useNavigate()
  const [deleteDeckDialogOpen, setDeleteDeckDialogOpen] = useState<boolean>(false)
  const [cardToDelete, setCardToDelete] = useState<number>(-1)
  const [deleteCardDialogOpen, setDeleteCardDialogOpen] = useState<boolean>(false)
  const [alreadyCopiedDeckId, setAlreadyCopiedDeckId] = useState<number>(-1)
  const {enqueueSnackbar} = useSnackbar()
  const [loginSignupOpen, setLoginSignupOpen] = useState<boolean>(false)
  const {user} = useAuth()
  const isAnon = !user

  useEffect(() => {
    let isCanceled = false

    api.getDeck(parseInt(id!)).then((deckResponse) => {
      if (deckResponse.isError()) {
        console.error('error getting deck', deckResponse.exception?.toJson())
        navigate('/error')
        return
      }

      if (isCanceled) return

      const deckFromApi = deckResponse.data!
      
      if (publicDeckView && !isAnon) {
        api.getCopiedId(deckFromApi.id).then(copiedIdRes => {
          if (copiedIdRes.isError()) {
            console.error('error checking if user already has copied deck')
            return
          }
          
          if (copiedIdRes.data!.id) {
            setAlreadyCopiedDeckId(copiedIdRes.data!.id)
          }
        })
      }
      
      setDeck(deckFromApi)
      deckFromApi.cards.forEach(c => c.createdAt = new Date(c.createdAt))
      deckFromApi.cards.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
      setCards(deckFromApi.cards)
    }).finally(() => {
      if (!isCanceled) setLoading(false)
    })

    return () => {
      isCanceled = true
    }
  }, [])

  const handleDeleteDeck = async () => {
    if (loading) {
      return
    }
    setLoading(true)
    setDeleteDeckDialogOpen(false)

    try {
      await api.deleteDeck(deck!.id)
    } catch (err) {
      console.error('error deleting deck')
      return
    } finally {
      setLoading(false)
    }

    navigate('/my-decks')
  }

  const handleDeleteCardConfirmation = (cardId: number) => {
    setCardToDelete(cardId)
    setDeleteCardDialogOpen(true)
  }

  const deleteCard = async () => {
    if (loading) {
      return
    }
    setLoading(true)
    setDeleteCardDialogOpen(false)

    try {
      await api.deleteCard(deck!.id, cardToDelete)
      setCards(prev => prev.filter(c => c.id !== cardToDelete))
      enqueueSnackbar('Card deleted', {variant: 'success'})
    } catch (err) {
      enqueueSnackbar('Error deleting card', {variant: 'error'})
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateCardEnabled = async (cardId: number, enabled: boolean) => {
    setLoading(true)
    await api.updateCardEnabled(deck!.id, cardId, enabled)
    let cardsToUpdate = [...cards]
    cards.filter(c => c.id === cardId)[0].enabled = enabled
    setCards(cardsToUpdate)
    setLoading(false)
  }
  
  const copyToMyDecks = async () => {
    if (isAnon) {
      handleAddPublicDeckAnon(deck?.id || -1)
      return
    }
    
    if (loading) return
    setLoading(true)
    const res = await api.copyDeck(deck!.id)
    if (res.isError() || res.data === null) {
      setLoading(false)
      enqueueSnackbar('Error copying deck', {variant: 'error'})
      return
    }
    enqueueSnackbar('Copied deck', {variant: 'success'})
    navigate(`/redirect/deck?deckId=${res.data.id}`)
  }

  const handleAddPublicDeckAnon = (deckId: number) => {
    setLoginSignupOpen(true)
  }

  const closeAnonDialog = () => {
    setLoginSignupOpen(false)
  }

  return (
    <>
      <LoadingBackdrop loading={loading}/>
      <AnonAddDeckDialog deckId={-1} open={loginSignupOpen} handleCloseCancel={closeAnonDialog}/>
      {deck !== null &&
        <div>
          <Paper
            sx={{
              p: 2, mt: 1, flexGrow: 1
            }}>
            <Grid container spacing={1}>
              <Grid item md={4}>
                <Box
                  component="img"
                  sx={{
                    maxWidth: 1,
                    px: 1,
                    pt: 1
                  }}
                  alt="Title Image"
                  src={DeckLogic.getImageUrl(deck)}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container>

                  <Grid item xs={9} md={10}>
                    <Box sx={{ml: 1}}>
                      <Typography variant='h3'>{deck.title}</Typography>
                      <Typography variant='body1'>{deck.description}</Typography>
                    </Box>
                  </Grid>

                  {!publicDeckView &&

                    <Grid item xs={3} md={2}
                          sx={{display: "flex", alignText: "top", alignItems: "top", justifyContent: "right"}}>
                      <div><IconButton onClick={_ => navigate('/edit-deck/' + deck!.id)}><EditIcon/></IconButton></div>
                      <div><IconButton onClick={() => setDeleteDeckDialogOpen(true)}><DeleteIcon
                        sx={{color: pink[600]}}/></IconButton></div>
                      <DeleteDialog
                        open={deleteDeckDialogOpen}
                        title="Delete Deck"
                        message="Are you sure you want to delete this deck and all of its cards?"
                        handleCloseDelete={handleDeleteDeck}
                        handleCloseCancel={() => setDeleteDeckDialogOpen(false)}
                      />
                    </Grid>
                  }
                  <Grid item xs={12} sx={{mt: {md: 3, xs: 1}}}>
                    <TagsDisplay tags={deck.tags}/>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{px: .5, pb: .5}}>
                {!publicDeckView &&
                  <Box sx={{display: "flex"}}>
                    <PlayButton deckId={deck.id} playType="normal" text="Study"/>
                    <PlayButton deckId={deck.id} playType="reversed" text="Reversed"/>
                  </Box>
                }
                {publicDeckView && alreadyCopiedDeckId === -1 && <Button1 onClick={copyToMyDecks}>Copy to My Decks</Button1>}
                {publicDeckView && alreadyCopiedDeckId > 0 && <Button1 onClick={_ => navigate(`/redirect/deck?deckId=${alreadyCopiedDeckId}`)}>Go To My Copy</Button1>}
              </Grid>

            </Grid>
          </Paper>

          <Box sx={{mt: 3}}>
            <Typography sx={{mb: 2}} variant='h5'>Cards</Typography>
          </Box>

          {cards.length === 0 && <p>no cards yet</p>}

          {cards.length > 0 &&
            <div>
              {cards.map(c => <CardDisplay
                deckId={deck.id}
                card={c}
                key={c.id}
                onDeleteCard={_ => handleDeleteCardConfirmation(c.id)}
                onUpdateCardEnabled={(cardId, enabled) => handleUpdateCardEnabled(cardId, enabled)}
                isPublicDeckView={publicDeckView}
              />)}

            </div>
          }

          <DeleteDialog
            open={deleteCardDialogOpen}
            title="Delete Card"
            message="Are you sure you want to delete this card?"
            handleCloseDelete={deleteCard}
            handleCloseCancel={() => setDeleteCardDialogOpen(false)}
          />
          {!publicDeckView &&
            <Box sx={{width: '200px'}}>
              <Button1
                startIcon={<AddIcon sx={{mb: 0.1, mr: 0}}/>}
                onClick={_ => navigate(`/deck/${id}/new-card`)}>
                New Card
              </Button1>
            </Box>
          }
        </div>}
    </>
  )
}

interface CardDisplayProps {
  deckId: number
  card: Card
  onDeleteCard: (cardId: number) => void
  onUpdateCardEnabled: (cardId: number, enabled: boolean) => void
  isPublicDeckView: boolean
}

function CardDisplay(props: CardDisplayProps) {
  let card = props.card
  const navigate = useNavigate()

  const updateCardEnabled = async (e: any, cardId: number, enabled: boolean) => {
    e.preventDefault()
    props.onUpdateCardEnabled(cardId, enabled)
  }

  return (
    <>
      <Box sx={{backgroundColor: "#000000",}}>
        <Paper
          elevation={3}
          sx={{
            my: 1,
            flexGrow: 1,
            opacity: card.enabled ? 1 : 0.45

          }}>
          <Grid container spacing="1" alignItems="top" sx={{pl: 1, py: 1}}>

            <Grid item sm={12} md={9} justifyContent='left'>
              <Grid container>
                <Grid item xs={12} sm={6} sx={{display: "flex"}}>

                  {card.frontImageMeta &&
                    <Box sx={{mr: 1}}>
                      <Avatar variant='rounded' src={ImageLogic.getImageUrl(card.frontImageMeta)}
                              sx={{width: {xs: 40, sm: 56}, height: {xs: 25, sm: 37}}}/>
                    </Box>
                  }

                  <Box sx={{mr: 1}}>
                    {card.frontText && <MarkdownDisplay value={card.frontText}/>}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} sx={{display: "flex", my: {xs: 1, sm: 0}}}>
                  {card.backImageMeta &&
                    <Box sx={{mr: 1}}>
                      <Avatar
                        variant='rounded' src={ImageLogic.getImageUrl(card.backImageMeta)}
                        sx={{width: {xs: 40, sm: 56}, height: {xs: 25, sm: 37}}}/>
                    </Box>
                  }

                  <Box sx={{mr: 1}}>
                    {card.backText && <MarkdownDisplay value={card.backText}/>}
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {!props.isPublicDeckView &&

              <Grid item xs={12} md={3}
                    sx={{alignText: "top", alignItems: "top", justifyContent: {xs: "left", sm: "right"}}}>
                <Grid container
                      sx={{display: "flex", alignText: "top", alignItems: "top", justifyContent: {xs: "space-between", sm: "flex-end"}, mt: {xs: 2, sm: 0}}}>

                  <Grid item sx={{mt: .5,}}><ConsecutiveCorrect card={card}/> </Grid>
                  <Grid item sx={{mx: 1, display: "flex"}}>
                    <div>
                      {card.enabled && <IconButton aria-label="disable" onClick={e => updateCardEnabled(e, card.id, false)}><VisibilityIcon/></IconButton>}
                      {!card.enabled && <IconButton aria-label="disable" onClick={e => updateCardEnabled(e, card.id, true)}><VisibilityOffIcon/></IconButton>}
                    </div>
                    <div>
                      <IconButton aria-label="delete" onClick={_ => navigate(`/deck/${props.deckId}/edit-card/${card.id}`)}><EditIcon/></IconButton>
                    </div>
                    <div>
                      <IconButton onClick={() => props.onDeleteCard(card.id)} aria-label="delete"><DeleteIcon sx={{color: pink[600]}}/></IconButton>
                    </div>
                  </Grid>
                </Grid>

              </Grid>
            }

          </Grid>
        </Paper>
      </Box>

    </>
  )
}
